import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import { Modal, Button, Container, ListGroup, Row, Col, Card } from "react-bootstrap";
import DoctorNavbar from "./DoctorNavbar";
import DoctorSidebar from "./DoctorSidebar";
import DoctorApprovalModal from "./DoctorApprovalModal";

const DoctorDashboard = ({ doctor }) => {
  const [queries, setQueries] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [notificationCount, setNotificationCount] = useState(0);
  const [approvedCount, setApprovedCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [displayApprovedCount, setDisplayApprovedCount] = useState(0);
  const [displayPendingCount, setDisplayPendingCount] = useState(0);
  const [displayTotalCount, setDisplayTotalCount] = useState(0);
  const lastQueryIdRef = useRef(null);

  const fetchQueries = async () => {
    try {
      const res = await axios.get(
        `https://ricgop.online/api/get-doctor-queries.php?doctorId=${doctor.id}`
      );
      const data = res.data;
      setQueries(data);

      if (data.length > 0) {
        const latestId = parseInt(data[0].id);
        if (lastQueryIdRef.current && latestId > lastQueryIdRef.current) {
          setNotificationCount((prev) => prev + 1);
        }
        lastQueryIdRef.current = latestId;

        const approved = data.filter((q) => Number(q.doctor_approved) === 1).length;
        const pending = data.filter((q) => Number(q.doctor_approved) === 0).length;
        const total = data.length;

        setApprovedCount(approved);
        setPendingCount(pending);
        setTotalCount(total);
      }
    } catch (error) {
      console.error("Error fetching queries:", error);
    }
  };

  useEffect(() => {
    fetchQueries();
    const interval = setInterval(fetchQueries, 10000);
    return () => clearInterval(interval);
  }, [doctor.id]);

  // Count animations
  useEffect(() => {
    animateCount(approvedCount, setDisplayApprovedCount);
  }, [approvedCount]);

  useEffect(() => {
    animateCount(pendingCount, setDisplayPendingCount);
  }, [pendingCount]);

  useEffect(() => {
    animateCount(totalCount, setDisplayTotalCount);
  }, [totalCount]);

  const animateCount = (target, setDisplay) => {
    let start = 0;
    const step = Math.ceil(target / 20);
    const interval = setInterval(() => {
      start += step;
      if (start >= target) {
        setDisplay(target);
        clearInterval(interval);
      } else {
        setDisplay(start);
      }
    }, 30);
  };

  const handleEventClick = (info) => {
    const query = queries.find((q) => q.id.toString() === info.event.id);
    setSelectedQuery(query || null);
    setShowModal(true);
  };

  const events = queries.map((q) => ({
    id: q.id.toString(),
    title: q.patient_name || "Patient Query",
    start: `${q.date}T${q.time}`,
    color: Number(q.doctor_approved) === 1 ? "#28a745" : "#ffc107"
  }));

  return (
    <div className="d-flex min-vh-100 bg-light">
      <DoctorSidebar />
      <main className="flex-grow-1 d-flex flex-column">
        <DoctorNavbar doctor={doctor} notificationCount={notificationCount} />
        <Container className="py-4 flex-grow-1">
          <div className="text-center mb-4">
            <h3 className="fw-bold text-primary">Patient Appointments</h3>
            <p className="text-muted mb-1">Welcome, Dr. {doctor.name}</p>
            <small className="text-muted">
              {doctor.specialization} • {doctor.email}
            </small>
          </div>

          <Row className="mb-4 g-3 justify-content-center">
            <Col md={4}>
              <Card className="text-center shadow stat-card border-start border-success border-4">
                <Card.Body>
                  <h5 className="text-success">Approved Appointments</h5>
                  <h2 className="fw-bold">{displayApprovedCount}</h2>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="text-center shadow stat-card border-start border-warning border-4">
                <Card.Body>
                  <h5 className="text-warning">Pending Appointments</h5>
                  <h2 className="fw-bold">{displayPendingCount}</h2>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="text-center shadow stat-card border-start border-primary border-4">
                <Card.Body>
                  <h5 className="text-primary">Total Appointments</h5>
                  <h2 className="fw-bold">{displayTotalCount}</h2>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <div className="calendar-wrapper bg-white rounded-4 shadow-sm p-4">
            <FullCalendar
              plugins={[dayGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              events={events}
              eventClick={handleEventClick}
              height="auto"
            />
          </div>
        </Container>

        <footer className="bg-white text-center py-3 border-top shadow-sm">
          <p className="mb-0 text-muted">&copy; 2025 Doctor Portal</p>
        </footer>
      </main>

      <DoctorApprovalModal
        show={showModal}
        onHide={() => setShowModal(false)}
        selectedQuery={selectedQuery}
        setSelectedQuery={setSelectedQuery}
        refreshQueries={fetchQueries}
        doctor={doctor}
      />

      <style>{`
        .calendar-wrapper {
          border-radius: 16px;
        }
        .stat-card {
          border-radius: 20px;
        }
        .modal-header-custom {
          background: linear-gradient(135deg, #007bff, #00bfff);
          color: #fff;
        }
        .modal-body-custom {
          padding: 1.5rem;
          background: #f9fafe;
        }
        .modal-footer-custom {
          background: #f1f3f5;
        }
          .calendar-wrapper {
          border-radius: 16px;
        }
        .modern-modal .modal-content {
          border-radius: 20px;
          box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15);
          background: #fff;
        }
        .modal-header-custom {
          background: linear-gradient(135deg, #007bff, #00bfff);
          color: #fff;
          border-bottom: none;
        }
        .modal-title-custom {
          font-size: 1.4rem;
          font-weight: 600;
        }
        .modal-body-custom {
          padding: 1.5rem;
          background: #f9fafe;
        }
        .modal-footer-custom {
          background: #f1f3f5;
          border-top: none;
        }
        .document-link {
          color: #007bff;
          text-decoration: underline;
        }
        .stat-card {
          border-radius: 20px;
        }
          .sidebar .nav-link.active,
        .sidebar .nav-link:hover {
          background-color: #eef6ff;
          color: #0d6efd;
          border-radius: 12px;
        }
        .glass-nav {
          background: rgba(255, 255, 255, 0.9);
          backdrop-filter: blur(8px);
        }
        .calendar-wrapper {
          border-radius: 16px;
        }
        .portfolio-link {
          background: linear-gradient(90deg, #f8f9fa, #e9f5ff);
          font-weight: 500;
          transition: all 0.3s ease;
        }
        .portfolio-link:hover {
          background: linear-gradient(90deg, #e0f0ff, #d1e9ff);
          color: #0d6efd;
          transform: translateX(5px);
        }
          /* Modern Modal */
        .modern-modal .modal-content {
        border-radius: 20px;
        box-shadow: 0 15px 40px rgba(0, 0, 0, 0.2);
        border: none;
        background: #ffffff;
        font-family: 'Segoe UI', sans-serif;
        }

        /* Header */
        .modal-header-custom {
        background: linear-gradient(135deg, #007bff, #00bfff);
        color: white;
        padding: 1rem 1.5rem;
        border-bottom: none;
        }

        .modal-title-custom {
        font-size: 1.4rem;
        font-weight: 600;
        }

        /* Body */
        .modal-body-custom {
        padding: 2rem 1.5rem;
        background-color: #f9fafe;
        }

        .modal-item {
        background: #ffffff;
        border: none;
        margin-bottom: 0.5rem;
        font-size: 0.95rem;
        display: flex;
        align-items: center;
        }

        /* Footer */
        .modal-footer-custom {
        background-color: #f1f3f5;
        border-top: none;
        padding: 1rem 1.5rem;
        }

        /* Link */
        .document-link {
        color: #007bff;
        text-decoration: underline;
        }

        /* Bootstrap Icons */
        .bi {
        color: #6c757d;
        font-size: 1.1rem;
        }
        .calendar-wrapper {
          border-radius: 16px;
        }
        .modern-modal .modal-content {
          border-radius: 20px;
          box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15);
          background: #fff;
        }
        .modal-header-custom {
          background: linear-gradient(135deg, #007bff, #00bfff);
          color: #fff;
          border-bottom: none;
        }
        .modal-title-custom {
          font-size: 1.4rem;
          font-weight: 600;
        }
        .modal-body-custom {
          padding: 1.5rem;
          background: #f9fafe;
        }
        .modal-footer-custom {
          background: #f1f3f5;
          border-top: none;
        }
        .document-link {
          color: #007bff;
          text-decoration: underline;
        }
        .modal .modal-dialog {
        margin-bottom: 0;
        margin-top: 0;
        max-width: 50%;
        width: 50%; }
      `}</style>
    </div>
  );
};

const DoctorDashboardWrapper = () => {
  const [doctor, setDoctor] = useState(null);
  useEffect(() => {
    const doctorData = localStorage.getItem("doctor");
    if (doctorData) {
      setDoctor(JSON.parse(doctorData));
    }
  }, []);
  if (!doctor) return <div className="text-center my-5 fs-5 text-muted">Loading doctor info...</div>;
  return <DoctorDashboard doctor={doctor} />;
};

export default DoctorDashboardWrapper;
